/**
 * Copyright © 2026 Adnuntius AS.
 */
import _ from 'lodash';
import moment from 'moment';
import {productCreativeTag} from './line-item-commons';

const objectiveStatRefs: Record<string, {stat: string; translation: string}> = {
  IMPRESSION: {stat: 'impressions', translation: 'impressions'},
  CLICK: {stat: 'clicks', translation: 'clicks'},
  VISIBLE_IMPRESSION: {stat: 'visibleImpressions', translation: 'visibles'},
  VIEWABLE_IMPRESSION: {stat: 'viewableImpressions', translation: 'viewables'},
  RENDERED_IMPRESSION: {stat: 'renderedImpressions', translation: 'rendered'}
};

export interface LineItemSubmitError {
  withApi: boolean;
  text: string;
  code: string;
  context: string;
}

export interface PrepareLineItemForSubmitParams {
  editingModel: any;
  persistedModel: any;
  clientErrors: LineItemSubmitError[];
  viewType: number;
  isAxPublisher: boolean;
  isLineItemTemplate: boolean;
  burnRateLimitsType: string;
  data?: any;
  networkTag: string;
  isSelfService: boolean;
  isNew: boolean;
  showCpa: boolean;
  now?: Date;
  modDates: () => void;
  objectiveLabel: (translation: string) => string;
  formatStat: (value: number) => string;
  incrementLineItemTemplatesCount: () => void;
}

export function prepareLineItemForSubmit(params: PrepareLineItemForSubmitParams): boolean {
  const {
    editingModel,
    persistedModel,
    clientErrors
  } = params;

  params.modDates();
  delete editingModel.objectiveDelivery; // failsafe for a bug in API deserialisation

  if (!_.trim(_.get(editingModel, 'name') || '')) {
    clientErrors.push({withApi: true, text: 'Name is required', code: 'required', context: 'name'});
  }

  if (params.viewType === 2) {
    editingModel.skipTeamBasedTargetingType = ['SITE_COUNTRY'];
  } else {
    editingModel.skipTeamBasedTargeting = true;
  }

  if (params.isAxPublisher) {
    delete editingModel.order;
  }

  if (params.isLineItemTemplate) {
    if (editingModel.teamType === 'ORDER') {
      editingModel.teams = [];
    } else {
      editingModel.order = null;
    }
  }

  if (params.burnRateLimitsType === 'SINGLE') {
    editingModel.burnRateLimits = (editingModel.burnRateLimits || []).slice(0, 1);
  } else if (params.burnRateLimitsType === 'VARIABLE') {
    editingModel.burnRateLimits = (editingModel.burnRateLimits || []).slice(0, 4);
  } else {
    editingModel.burnRateLimits = [];
  }

  if (_.isDate(editingModel.endDate)) {
    const endDateChanged = !_.isDate(persistedModel.endDate)
      || persistedModel.endDate.getTime() !== editingModel.endDate.getTime();
    const now = params.now ? moment(params.now) : moment();
    if (endDateChanged && moment(editingModel.endDate).isBefore(now)) {
      clientErrors.push({
        withApi: true,
        text: 'The end date must be in the future',
        code: 'endDate',
        context: 'resource.model.endDate'
      });
    }
  }

  if (params.data) {
    _.forEach(editingModel.objectives, (num, objectiveType) => {
      const statRef = objectiveStatRefs[objectiveType];
      if (!statRef) {
        return;
      }
      const alreadyDelivered = params.data[statRef.stat];
      const previousNum = _.get(persistedModel, ['objectives', objectiveType]);
      if (_.isNumber(num) && (!_.isNumber(previousNum) || num !== previousNum) && num <= alreadyDelivered) {
        clientErrors.push({
          withApi: true,
          text: 'The ' + params.objectiveLabel(statRef.translation).toLowerCase()
            + ' objective must be more than ' + params.formatStat(alreadyDelivered),
          code: 'overDone' + objectiveType,
          context: 'lineItem.objectives.' + objectiveType
        });
      }
    });
  }

  _.forEach(editingModel.rateLimits, (rateLimit) => {
    if (rateLimit.type === 'BUDGET') {
      rateLimit.scope = 'GLOBAL';
      rateLimit.smoothed = false;
      rateLimit.periodType = 'DAYS';
    }
  });

  if (_.get(editingModel, ['bidSpecification', 'optimiseFor', 'eventType']) === 'CUSTOM') {
    const customEventId = params.networkTag === '107727' ? '2lxhrk175x3m68gs' : '1q9qmf5z0b2ry1qd';
    editingModel.bidSpecification.optimiseFor.customEventType = {id: customEventId};
  } else if (_.get(editingModel, ['bidSpecification', 'optimiseFor', 'customEventType'])) {
    editingModel.bidSpecification.optimiseFor.customEventType = null;
  }

  if (editingModel.type === 'CREATIVE_TAG'
    || _.get(editingModel, ['axProduct', 'type']) === 'CREATIVE_TAG'
    || _.get(editingModel, ['axProduct', 'id']) === productCreativeTag) {
    _.set(editingModel, ['bidSpecification', 'bidStrategy'], null);
    _.set(editingModel, ['type'], 'CREATIVE_TAG');
    const defaultCurrency = _.get(editingModel, ['team', 'defaultCurrency']);
    if (defaultCurrency && !_.get(editingModel, ['billingCurrency'])) {
      _.get(editingModel, ['billingCurrency'], defaultCurrency);
    }
  }

  // We no longer send the smoothed flag from the UI
  delete editingModel.smoothed;
  delete persistedModel.smoothed;

  if (clientErrors.length > 0) {
    return false;
  }

  if (params.isSelfService) {
    delete editingModel.tier;
  }

  if (!_.isFinite(editingModel.sponsorshipPercentage)) {
    editingModel.sponsorshipPercentage = null;
  }

  if (!_.get(editingModel, ['objectives', 'BUDGET', 'amount'])) {
    _.set(editingModel, ['objectives', 'BUDGET'], null);
  }

  if (!_.get(editingModel, ['election', 'id'])) {
    _.set(editingModel, ['politicalCampaignTopic'], null);
  }

  if (params.isNew && params.isLineItemTemplate) {
    params.incrementLineItemTemplatesCount();
  }

  if (!params.showCpa) {
    editingModel.bidSpecification = editingModel.bidSpecification || {};
    editingModel.bidSpecification.cpa = {};
  }

  return true;
}
